import * as React from "react";
import { inject, observer } from "mobx-react";
import { AdvertisingStore } from "src/stores/AdvertisingStore";
import { UitkPlaceholderCard } from "uitk-react-cards";
import { Logger } from "bernie-logger";
import { logger } from "bernie-core";
import { MESO_AD_LOAD_FAIL } from "src/config/systemEvents";

enum view {
  ad,
  loader,
  fallback,
}

const mesoAdEventName = "mesoAd";
const mesoFallbackEventName = "mesoFallback";

interface NativeAdProps {
  uci: string;
  type: string;
  children?: JSX.Element[] | JSX.Element;
  advertising?: AdvertisingStore;
  className?: string;
  logger?: Logger;
  showFallback?: boolean;
}

const jsonToSplunkFriendlyString = (json: { [x: string]: any }) =>
  Object.keys(json)
    .map((key) => `${key}=${json[key]}`)
    .join(" ");

export const NativeAd: React.SFC<NativeAdProps> = logger("NativeAd")(
  inject("advertising")(
    observer((props: NativeAdProps) => {
      const { uci, type, children } = props;
      const mesoNative: React.RefObject<JSX.IntrinsicElements["meso-native"]> = React.createRef();
      const [initialView, setCurrentView] = React.useState(view.loader);
      let currentView = initialView;

      if (props.showFallback || (props.advertising && props.advertising.adBlockerEnabled === true)) {
        currentView = view.fallback;
      }

      const removeWindowdLoadEventHandler = () => {
        if (window) {
          window.removeEventListener(mesoFallbackEventName, handleWindowAdLoadEvent);
        }
      };

      const removeAdLoadEventHandler = () => {
        if (mesoNative && mesoNative.current) {
          mesoNative.current.removeEventListener(mesoAdEventName, handleAdLoadEvent);
        }
      };

      const handleWindowAdLoadEvent = (event: CustomEvent) => {
        if (event && event.detail && event.detail.code === "FETCH-ADS-FAIL") {
          if (props.logger && props.advertising && props.advertising.adBlockerEnabled !== true) {
            props.logger.logEvent(
              MESO_AD_LOAD_FAIL,
              "failed to fetch MeSo ad",
              jsonToSplunkFriendlyString(event.detail)
            );
          }
          setCurrentView(view.fallback);
          removeWindowdLoadEventHandler();
        }
      };

      const handleAdLoadEvent = (event: CustomEvent) => {
        if (
          props.logger &&
          event.detail &&
          event.detail.code !== "AD-LOAD-PASS" &&
          props.advertising &&
          props.advertising.adBlockerEnabled !== true
        ) {
          props.logger.logEvent(MESO_AD_LOAD_FAIL, "failed to fetch MeSo ad", jsonToSplunkFriendlyString(event.detail));
        }
        setCurrentView(event.detail && event.detail.code === "AD-LOAD-PASS" ? view.ad : view.fallback);
        removeAdLoadEventHandler();
      };

      React.useEffect(() => {
        if (props.advertising) {
          props.advertising.includeAd();
        }
        if (mesoNative && mesoNative.current) {
          mesoNative.current.addEventListener(mesoAdEventName, handleAdLoadEvent);
        }
        if (window) {
          window.addEventListener(mesoFallbackEventName, handleWindowAdLoadEvent);
        }

        return removeAdLoadEventHandler;
      }, [mesoNative.current]);

      return (
        <React.Fragment>
          <div className={props.className}>
            <meso-native
              class={currentView === view.ad ? `${props.className}` : "is-visually-hidden"}
              uci={uci}
              nativetype={type}
              isfluid={true}
              adslot={uci}
              data-testid="meso-native"
              ref={mesoNative}
            />
            {currentView === view.loader && <UitkPlaceholderCard className={props.className} />}
            {currentView === view.fallback ? children : null}
          </div>
        </React.Fragment>
      );
    })
  )
);
